/**
 * What the account screen may say about the current plan (STRIPE Phase 3, product decision D2).
 *
 * v3 `GET /users/{id}` → `package_detail[]` carries `subscription_status` (raw Stripe status), `canceled_at` (Stripe
 * `cancel_at` — a scheduled cancellation, cleared on resume) and `end_date` (`current_period_end`). Past-due rows are
 * listed; fully canceled and ended rows are not.
 *
 * Precedence:
 *  1. a payment problem (`past_due` / `unpaid` / `incomplete`) — access is already denied with no grace period, so the
 *     screen explains it and offers payment recovery; it must NEVER say "renews … you will be charged";
 *  2. a canceled subscription, or a period that already ended (date level) → "ended on" — never "renews";
 *  2b. a cancellation mark → "ends on" that date;
 *  2t. a trial — the row's own `billing_type: "trial"` (v3 provisions a trial as its own `<sub>_trial` row) or a
 *      `trialing` status → "free trial ends on end_date" (the trial window's end), and, when Stripe will bill the plan
 *      after it, what it then charges (STRIPE P6 FE-1: the screen used to read "active until" / "renews" with no trial
 *      word at all). A cancellation mark still wins (2b): a trial canceled in the Portal ends, it does not convert;
 *  2c. a change scheduled for the period end (`scheduled_change.effective_at`, v3 P5-B1 / PD1) → "changes on" that date —
 *      the row still IS the current plan until then, and "renews … for total_price" would quote today's price;
 *  3. a priced plan that Stripe bills (`billing_source: "stripe"`) → "renews on end_date for total_price";
 *  4. otherwise (manual / demo / comp, no price) → "active until end_date".
 *
 * P7 F2-B6 → F2-F1 (additive verdicts from v3; each absent on an older backend, which then reads exactly as above):
 *  - `cancel_at` is the scheduled end (preferred over the legacy `canceled_at`); `non_renewing_reason` says WHY it ends
 *    (`user_cancel` / `contract` / `trial_no_card`) and rides on `ends`;
 *  - `trial_no_card` → the trial-ends text, never a conversion charge;
 *  - `managed_until` (only while v3's K11 flag is on) → "managed by Refabric until X";
 *  - `renews: false` on a Stripe-billed row with no scheduled end → "ends on end_date", never "renews". A manual row keeps
 *    "active until" (it never promised a renewal). `renews: null` (unknown) changes nothing.
 *
 * A row from a backend that sends no `subscription_status` falls through to 2–4: exactly the previous behaviour.
 * STRIPE P5 / PD10: a row WITHOUT `billing_source: "stripe"` never reads as a renewal — v3 sends the field on every row,
 * so its absence is not proof that Stripe will charge anyone.
 */

export type PlanStatus =
  | { kind: "none" }
  | { kind: "payment_problem" }
  | { kind: "ended"; date: string }
  | { kind: "ends"; date: string; reason?: NonRenewingReason }
  | { kind: "managed"; date: string }
  | { kind: "trial"; date: string; price?: number }
  | { kind: "changes"; date: string }
  | { kind: "renews"; date: string; price: number }
  | { kind: "active_until"; date: string };

type NonRenewingReason = "user_cancel" | "contract" | "trial_no_card";

const NON_RENEWING_REASONS: ReadonlySet<string> = new Set<NonRenewingReason>(["user_cancel", "contract", "trial_no_card"]);

export interface PlanStatusRow {
  subscription_status?: string | null;
  // The subscription ROW's own `package_type` (`paid` | `trial` | `addon`), aliased by v3 away from `package_type`.
  billing_type?: string | null;
  canceled_at?: string | null;
  end_date?: string | null;
  total_price?: number | null;
  // v3 (STRIPE Phase 3 item 8): "stripe" | "manual". Backoffice demo / agreement / comp rows are stored as package_type
  // "paid" too, so ONLY `billing_source === "stripe"` is Stripe-billed. Absent or unknown → not Stripe-billed (PD10).
  billing_source?: string | null;
  scheduled_change?: { effective_at?: string | null } | null;
  // P7 F2-B6 (see the header).
  cancel_at?: string | null;
  renews?: boolean | null;
  non_renewing_reason?: string | null;
  managed_until?: string | null;
}

const PAYMENT_PROBLEM_STATUSES: ReadonlySet<string> = new Set(["past_due", "unpaid", "incomplete"]);

export const isPaymentProblemStatus = (status: string | null | undefined): boolean => !!status && PAYMENT_PROBLEM_STATUSES.has(status.toLowerCase());

/** The presented row is a trial: v3's own trial row, or a subscription Stripe still reports as trialing. */
export const isTrialRow = (row: Pick<PlanStatusRow, "billing_type" | "subscription_status">): boolean =>
  (row.billing_type ?? "").toLowerCase() === "trial" || (row.subscription_status ?? "").toLowerCase() === "trialing";

const isParseableDate = (value: string | null | undefined): value is string => !!value && !Number.isNaN(new Date(value).getTime());

/**
 * The row the account screen presents as "the plan": the first that is not the extra-credit pool. The backend already
 * orders `package_detail` (real before `sub_fake_*`, paid before trial, latest period first), so no FE priority is added.
 */
export const selectMainPlan = <T extends { package_type?: string | null }>(rows: readonly T[] | null | undefined): T | undefined =>
  rows?.find((row) => row.package_type !== "refabric_credits") ?? rows?.[0];

/**
 * P7 F2-B6: the row v3 names as the primary plan (`primary_subscription_id`), so Plan & Billing and Manage Members present
 * the same plan. No id, or no row carrying it (an older backend) → `selectMainPlan`'s first-row rule, as before.
 */
export const selectPrimaryPlan = <T extends { package_type?: string | null; subscription_id?: string | null }>(
  rows: readonly T[] | null | undefined,
  primarySubscriptionId: string | null | undefined
): T | undefined => (primarySubscriptionId && rows?.find((row) => row.subscription_id === primarySubscriptionId)) || selectMainPlan(rows);

/** ANY plan row in a payment-problem state — the backend's past-due remedy keys on any of them, not on the presented row. */
export const hasPaymentProblem = (rows: readonly PlanStatusRow[] | null | undefined): boolean => !!rows?.some((row) => isPaymentProblemStatus(row.subscription_status));

// Date-level, in UTC — the same granularity as the backend's period filter (a period ending later today is still current).
const utcDay = (value: string | Date): string => new Date(value).toISOString().slice(0, 10);

export const resolvePlanStatus = (row: PlanStatusRow | null | undefined, now: Date = new Date()): PlanStatus => {
  if (!row) return { kind: "none" };

  if (isPaymentProblemStatus(row.subscription_status)) return { kind: "payment_problem" };

  // Reviewer 95: a canceled row — with or without a cancellation mark — and a period already over are ENDED.
  if ((row.subscription_status ?? "").toLowerCase() === "canceled") {
    return { kind: "ended", date: row.end_date ?? row.canceled_at ?? "" };
  }
  if (isParseableDate(row.end_date) && utcDay(row.end_date) < utcDay(now)) {
    return { kind: "ended", date: row.end_date };
  }

  const reason = row.non_renewing_reason && NON_RENEWING_REASONS.has(row.non_renewing_reason) ? (row.non_renewing_reason as NonRenewingReason) : undefined;
  const withReason = reason ? { reason } : {};

  // F2-B6: a card-less trial lapses at the end of its window — the trial text, with no conversion charge.
  if (reason === "trial_no_card") return { kind: "trial", date: row.end_date ?? row.cancel_at ?? row.canceled_at ?? "" };

  const cancelAt = row.cancel_at || row.canceled_at;
  if (cancelAt) {
    // A mark we cannot read still means "this plan is ending" — never a renewal; show the period end instead.
    return { kind: "ends", date: isParseableDate(cancelAt) ? cancelAt : row.end_date ?? cancelAt, ...withReason };
  }

  if (isParseableDate(row.managed_until)) return { kind: "managed", date: row.managed_until };

  const isStripeBilled = row.billing_source === "stripe";
  // F2-B6: Stripe will not renew it (strictly `false`; `null` = unknown keeps today's reading).
  if (isStripeBilled && row.renews === false) return { kind: "ends", date: row.end_date ?? "", ...withReason };

  // Stripe-billed rule as for a renewal (PD10): only a priced `billing_source: "stripe"` row may quote a charge.
  const stripePrice = Number(row.total_price) > 0 && isStripeBilled ? Number(row.total_price) : undefined;

  if (isTrialRow(row)) return stripePrice === undefined ? { kind: "trial", date: row.end_date ?? "" } : { kind: "trial", date: row.end_date ?? "", price: stripePrice };

  const effectiveAt = row.scheduled_change?.effective_at;
  if (isParseableDate(effectiveAt)) return { kind: "changes", date: effectiveAt };

  if (stripePrice !== undefined) return { kind: "renews", date: row.end_date ?? "", price: stripePrice };

  return { kind: "active_until", date: row.end_date ?? "" };
};
